import { z } from 'zod';

import { TENANT_BASE_DOMAIN } from '@/config';
import { SESSION_CHANGE_EVENT } from '@/lib/session';

const KEY = 'fleetava_impersonation';

const impersonationSchema = z.object({
  token: z.string().min(1),
  expiresAt: z.string(),
  agencyName: z.string(),
  slug: z.string(),
  user: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    role: z.string(),
    scope: z.enum(['agency', 'platform']),
    agencyId: z.number().nullable(),
  }),
});

export type ImpersonationSession = z.infer<typeof impersonationSchema>;

export function parseImpersonation(payload: unknown): ImpersonationSession {
  const parsed = impersonationSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The desk session could not be read.');
  return parsed.data;
}

export function impersonationHost(slug: string): string {
  return `${slug}.${TENANT_BASE_DOMAIN}`;
}

export function currentImpersonation(): ImpersonationSession | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = impersonationSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || Date.parse(parsed.data.expiresAt) <= Date.now()) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function storeImpersonation(session: ImpersonationSession): void {
  window.localStorage.setItem(KEY, JSON.stringify(session));
  window.dispatchEvent(new Event(SESSION_CHANGE_EVENT));
}

export function clearImpersonation(): void {
  window.localStorage.removeItem(KEY);
  window.dispatchEvent(new Event(SESSION_CHANGE_EVENT));
}
